"use client";

import { motion, useReducedMotion } from "framer-motion";
import { HeroOrganicMask } from "@/components/decorative/HeroOrganicMask";
import { PageDecorLayer } from "@/components/decorative/PageDecorLayer";
import { EASE_OUT_EXPO } from "@/components/motion/constants";
import { cn } from "@/lib/utils";

interface PageHeroImage {
  src: string;
  alt: string;
  imageClassName?: string;
}

interface PageHeroProps {
  eyebrow: string;
  title: string;
  accentWord?: string;
  subtitle: string;
  align?: "left" | "center";
  layout?: "center" | "split";
  image?: PageHeroImage;
  className?: string;
  children?: React.ReactNode;
}

export function PageHero({
  eyebrow,
  title,
  accentWord,
  subtitle,
  align = "center",
  layout = "center",
  image,
  className,
  children,
}: PageHeroProps) {
  const reduced = useReducedMotion();
  const isSplit = layout === "split" && image != null;
  const isCenter = !isSplit && align === "center";

  const fadeUp = (delay = 0) =>
    reduced
      ? {}
      : {
          initial: { opacity: 0, y: 20 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.75, ease: EASE_OUT_EXPO, delay },
        };

  const renderTitle = () => {
    if (!accentWord || !title.includes(accentWord)) {
      return title;
    }

    const parts = title.split(accentWord);
    return (
      <>
        {parts[0]}
        <span className="bg-gradient-to-r from-[var(--site-primary)] to-[var(--site-accent)] bg-clip-text text-transparent">
          {accentWord}
        </span>
        {parts[1]}
      </>
    );
  };

  const textContent = (
    <>
      <motion.p {...fadeUp(0)} className="site-eyebrow mb-3">
        {eyebrow}
      </motion.p>

      <motion.h1
        {...fadeUp(0.05)}
        className="font-display text-4xl font-bold leading-tight text-[var(--site-text)] md:text-5xl lg:text-6xl"
      >
        {renderTitle()}
      </motion.h1>

      <motion.p
        {...fadeUp(0.1)}
        className={cn(
          "mt-5 text-lg leading-relaxed text-[var(--site-muted)]",
          isCenter && "mx-auto max-w-2xl"
        )}
      >
        {subtitle}
      </motion.p>

      {children ? (
        <motion.div {...fadeUp(0.2)} className="mt-8">
          {children}
        </motion.div>
      ) : null}
    </>
  );

  return (
    <section
      aria-label="Page hero"
      className={cn(
        "relative mt-16 overflow-hidden pb-10 pt-12 md:pb-14 md:pt-16",
        className
      )}
    >
      <PageDecorLayer variant="hero" palette="site" />

      <div
        className={cn(
          "relative z-10 mx-auto max-w-7xl px-4 md:px-6",
          isSplit
            ? "grid gap-8 lg:grid-cols-2 lg:items-center lg:gap-12"
            : isCenter && "text-center"
        )}
      >
        <div className={cn(isSplit && align === "left" && "text-left")}>
          {textContent}
        </div>

        {isSplit && image ? (
          <div className="relative">
            <HeroOrganicMask
              src={image.src}
              alt={image.alt}
              imageClassName={image.imageClassName}
              palette="site"
              priority
              className="relative z-10"
            />
          </div>
        ) : null}
      </div>
    </section>
  );
}
